import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Select, Popconfirm, Row, Col } from 'antd';
import { isEmpty } from 'utils/utils';
import { useRefCode, useProjectDetail } from 'hooks';
import { CompanySelectBox, CrmSelectBox, RangePicker, LectureTarget, NotionUrl, Pm } from 'components/atoms'
import { RangeValue } from 'rc-picker/lib/interface';
import { dateFormat } from 'utils/date-utils';
import { getCodeArray, getCodeNumberArray } from 'utils/form-utils';
import moment, { Moment } from 'moment';
import { PROJECT_WORK_TYPE } from 'commons/constants';
import {
    CheckDuplicateModal,
    CheckProjectStepModal,
    ProjectAskSelectModal,
    ProjectAskEditModal
} from 'components/molecules';

import { fetcher } from 'services';
import { usePopupManager } from 'react-popup-manager';
import { IMaskInput } from "react-imask";

export interface ProjectEditModalProps extends PopupProps {
    id: number;
    pType: PROJECT_WORK_TYPE;    
}

const ProjectEditModal = ({ 
    isOpen, 
    onClose, 
    pType,
    id}: ProjectEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [companySrl, setCompanySrl] = useState<number>(0);
    const [askGroupSrl, setAskGroupSrl] = useState<number>(0);
    const [maxPrice, setMaxPrice] = useState<number>(0);
    const [companyName, setCompanyName] = useState<string>('');
    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();
    const [projectStep, setProjectStep] = useState<string>('A');
    const [dropReason, setDropReason] = useState<string>('');
    const [isCheckConfirm, setIsCheckConfirm] = useState<boolean>(false);
    const [isCheckConfirm2, setIsCheckConfirm2] = useState<boolean>(false);
    const [saveData, setSaveData] = useState<ProjectSaveReqDto>();
    const [message, setMessage] = useState<string>('');
    
  /* ======================================================
  * hooks
  * ====================================================== */
  const { refCodeList: statusTypeList } = useRefCode({field:'project_status_type'});  
  const { projectInfo, insertSuccess, updateSuccess, deleteSuccess, projectInsert, projectUpdate, projectDelete } = useProjectDetail(id);

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { open } = usePopupManager(); 

  const configBlocks = {
    PriceInput: {
      mask: Number,
      signed: true,  // disallow negative
      thousandsSeparator: ",",  // any single char
      padFractionalZeros: false,
      normalizeZeros: false,
      unmask: true // true|false|'typed'
    }
  }

   /* ======================================================
   * events
   * ====================================================== */
   const onCheckPopupClick = (isOk: boolean) => {
        if (isOk && saveData) {
            if (pType==='confirm' && projectStep === 'B') {
                setIsCheckConfirm2(true);
            } else {
                projectUpdate({...saveData,confirm:'Y'}) 
            }
            
        }   
        setIsCheckConfirm(false);
    }

    const onCheckPopupClick2 = (isOk: boolean) => {
        if (saveData) {
            projectUpdate({...saveData,confirm:'Y',delete_schedule:isOk?'Y':'N'}) 
        }   
        setIsCheckConfirm2(false);
    }

    const onCancel = () => {
        onClose?.(false);
    };

    const onCalcMaxPirce = () => {
        setMaxPrice(
            Number(form.getFieldValue('operating_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('plan_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('studio_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('etc_price').toString().replaceAll(',',''))
        )     
    }

    const onSubmit = (e) => {
        if (id > 0) {
            if (projectInfo && moment(projectInfo.created_at).isSameOrAfter('2026-09-11') && askGroupSrl == 0) {
                alert("문의를 등록해주세요.");   
                return;
            }

            const pdata = 
                {
                    id: id,
                    company_srl: e.company_srl, 
                    crm_srl: e.crm_srl,  
                    title: e.title,
                    start_date: startDate,
                    end_date: endDate, 
                    lecture_target: e.lecture_target?e.lecture_target.map((code) => code).join(','):null, 
                    target_memo: e.target_memo,                    
                    operating_price: Number(form.getFieldValue('operating_price').toString().replaceAll(',','')),
                    lecturer_price: Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')), 
                    diagnose_price: Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')), 
                    consulting_price: Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')), 
                    plan_price: Number(form.getFieldValue('plan_price').toString().replaceAll(',','')), 
                    studio_price: Number(form.getFieldValue('studio_price').toString().replaceAll(',','')), 
                    traffic_price: Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')), 
                    teaching_price: Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')), 
                    etc_price: Number(form.getFieldValue('etc_price').toString().replaceAll(',','')), 
                    notion_url: e.notion_url, 
                    status_type: 'confirm',  
                    people_count: e.people_count, 
                    project_step: e.project_step?e.project_step:projectStep,
                    work_type: String(pType),
                    pm: e.pm,
                    comment: e.comment,  
                    drop_reason: e.drop_reason,    
                    drop_reason_comment: e.drop_reason_comment,    
                    ask_group_srl: askGroupSrl,  
                }

            setSaveData(pdata);
            if (!!projectInfo && projectInfo.project_step != e.project_step) {
                if (pType==='consult' && e.project_step === 'A') setMessage('확정 상태로 변경 하시겠습니까?\n변경 시 등록된 스케줄은 강사 일정에 추가 됩니다.');
                if (pType==='consult' && e.project_step === 'C') setMessage('진행 협의 상태로 변경 하시겠습니까?\n변경시 등록된 스케줄은 삭제 됩니다.');
                if (pType==='consult' && e.project_step === 'D') setMessage('드롭 상태로 변경 하시겠습니까?\n변경 시 해당 내용과 정보는 드롭으로 이동됩니다.\n변경시 등록된 스케줄은 삭제 됩니다.');
                if (pType==='confirm' && e.project_step === 'B') setMessage('일정 논의 상태로 변경 하시겠습니까?\n변경시 등록된 스케줄은 강사 일정에서 삭제 됩니다.');
                if (pType==='confirm' && e.project_step === 'D') setMessage('드롭 상태로 변경 하시겠습니까?\n변경시 등록된 스케줄은 삭제 됩니다.');
                if (pType==='drop' && e.project_step === 'A') setMessage('확정 상태로 변경 하시겠습니까?\n변경 시 해당 내용과 정보는 확정으로 이동되며,\n세부 스케줄은 강사 일정에 추가 됩니다.');
                if (pType==='drop' && e.project_step === 'B') setMessage('일정 논의 상태로 변경 하시겠습니까?\n변경 시 해당 내용과 정보는 논의중으로 이동됩니다.');
                if (pType==='drop' && e.project_step === 'C') setMessage('진행 협의 상태로 변경 하시겠습니까?\n변경 시 해당 내용과 정보는 논의중으로 이동됩니다.');
                setIsCheckConfirm(true);
            } else {
                projectUpdate(pdata)  
            }
        } else {
            if (askGroupSrl == 0) {
                alert("문의를 등록해주세요.");   
                return;
            }
            projectInsert({
                id: 0,
                company_srl: e.company_srl, 
                crm_srl: e.crm_srl,  
                title: e.title,
                start_date: startDate,
                end_date: endDate, 
                lecture_target: e.lecture_target?e.lecture_target.map((code) => code).join(','):null, 
                target_memo: e.target_memo,
                operating_price: Number(form.getFieldValue('operating_price').toString().replaceAll(',','')),
                lecturer_price: Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')), 
                diagnose_price: Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')), 
                consulting_price: Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')), 
                plan_price: Number(form.getFieldValue('plan_price').toString().replaceAll(',','')), 
                studio_price: Number(form.getFieldValue('studio_price').toString().replaceAll(',','')), 
                traffic_price: Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')), 
                teaching_price: Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')), 
                etc_price: Number(form.getFieldValue('etc_price').toString().replaceAll(',','')),           
                notion_url: e.notion_url, 
                status_type: e.status_type?e.status_type:'confirm', 
                people_count: e.people_count, 
                project_step: e.project_step?e.project_step:projectStep,
                work_type: String(pType),
                pm: e.pm,
                comment: e.comment, 
                ask_group_srl: askGroupSrl,
              })
        }
    };     
    
    const onChangePeriod = (date: RangeValue<Moment>, dateString: [string, string]) => {
   
        const searchDateS = dateString[0] ? dateFormat(dateString[0], 'YYYY-MM-DD') : undefined;
        const searchDateE = dateString[1] ? dateFormat(dateString[1], 'YYYY-MM-DD') : undefined;
    
        setStartDate(searchDateS);
        setEndDate(searchDateE);
      };

    const onSelectAskPopup = () => {
        if (companySrl == 0) {
            alert("고객사를 선택해주세요.");   
            return;   
        }
        if (askGroupSrl > 0) {
            open(ProjectAskEditModal, {
                id: askGroupSrl,
            }); 
        } else {
            open(ProjectAskSelectModal, {
                company_srl: companySrl,
                onClose: (isOk, askId, crm_srl) => {
                    if (isOk) {
                        setAskGroupSrl(askId);
                        form.setFieldValue("crm_srl", crm_srl);
                    }
                },
            }); 
        }
    }         
  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!projectInfo) {            
            if (!!projectInfo.start_date && projectInfo.start_date !== '0000-00-00' && !!projectInfo.end_date && projectInfo.end_date !== '0000-00-00') {
                setStartDate(projectInfo.start_date);
                setEndDate(projectInfo.end_date);
            }   
            
            setMaxPrice(projectInfo.max_price);

            form.setFieldsValue({
                company_srl: projectInfo.company_srl || undefined, 
                crm_srl: projectInfo.crm_srl || undefined, 
                title: projectInfo.title || '', 
                lecture_target: getCodeArray(projectInfo.lecture_target) || [],
                target_memo: projectInfo.target_memo,
                operating_price: projectInfo.operating_price || 0,
                lecturer_price: projectInfo.lecturer_price || 0,
                diagnose_price: projectInfo.diagnose_price || 0,
                consulting_price: projectInfo.consulting_price || 0,
                plan_price: projectInfo.plan_price || 0,
                studio_price: projectInfo.studio_price || 0,
                traffic_price: projectInfo.traffic_price || 0,
                teaching_price: projectInfo.teaching_price || 0,
                etc_price: projectInfo.etc_price || 0,
                notion_url: projectInfo.notion_url || '', 
                status_type: projectInfo.status_type || 'confirm',
                people_count: projectInfo.people_count || 0, 
                project_date: (!!projectInfo.start_date && projectInfo.start_date !== '0000-00-00' && !!projectInfo.end_date && projectInfo.end_date !== '0000-00-00')?[moment(projectInfo.start_date, "YYYY-MM-DD"), moment(projectInfo.end_date, "YYYY-MM-DD")]:[], 
                project_step: projectInfo.project_step?projectInfo.project_step:(pType === 'confirm')?'A':'C', 
                pm: getCodeNumberArray(projectInfo.pm),
                comment: projectInfo.comment || '',
                drop_reason: projectInfo.drop_reason,
                drop_reason_comment: projectInfo.drop_reason_comment,
            });

            setCompanySrl(projectInfo.company_srl);
            setCompanyName(projectInfo.company_name);
            setProjectStep(projectInfo.project_step);
            setDropReason(projectInfo.drop_reason);
            setAskGroupSrl(projectInfo.ask_group_srl)
        }
    }, [projectInfo])

    useEffect(() => {
        if (insertSuccess || updateSuccess || deleteSuccess) {
            onClose?.(true);  
        }
    }, [insertSuccess, updateSuccess, deleteSuccess])

    useEffect(() => {
        if (pType === 'confirm') {
            form.setFieldValue('project_step', 'A');
        } else if (pType === 'consult') {
            form.setFieldValue('project_step', 'B');
        } else  {
            form.setFieldValue('project_step', 'D');
        }
        
        form.setFieldsValue({
            operating_price: 0,
            lecturer_price: 0,
            diagnose_price: 0,
            consulting_price: 0,
            plan_price: 0,
            studio_price: 0,
            traffic_price: 0,
            teaching_price: 0,
            etc_price: 0,
            people_count: 0, 
        });        
    }, [])

    return (
    <>
        <ModalWrapper open={isOpen} onCancel={onCancel} width={800} title={id===0?'프로젝트 등록':'프로젝트 수정'} closable>
            <Form
                form={form}
                name="ProjectInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Form.Item  label={<label className={'ant-form-item-required'}>고객사(기업명)</label>} className={'ant-form-item-required'}>
                    <Form.Item name="company_srl" rules={[{ required: true, message: '' }]} style={{ width:'auto', display: 'inline-block' }}>
                        <CompanySelectBox required={true} form={form} company_srl={companySrl} onSelected={(value, option) => {setCompanySrl(Number(value)); setCompanyName(option.label);}} />
                    </Form.Item>  
                    <Form.Item style={{ width: 'auto', display: 'inline-block'}}>
                        <Button style={{marginLeft:'5px'}} onClick={onSelectAskPopup} type="primary" danger = {askGroupSrl === 0}>{askGroupSrl === 0 ? `문의 연결` : `문의 보기`}</Button>
                        {(askGroupSrl > 0) && (
                            <Button style={{marginLeft:'5px'}} onClick={()=>setAskGroupSrl(0)} type="primary" danger = {true}>{`문의 삭제`}</Button>
                        )}
                    </Form.Item>                    
                </Form.Item >
                <Form.Item name="crm_srl" label={'교육담당자'} rules={[{ required: true, message: '' }]}>
                    <CrmSelectBox form={form} company={companySrl} company_name={companyName} required={true} />                  
                </Form.Item>  
                <Form.Item name="title" label={'프로젝트명'} rules={[{ required: true, message: '' }]}>
                    <Input type="text" placeholder='프로젝트명을 입력하세요'/>                                        
                </Form.Item>  
                <Form.Item name="project_date" label={'교육 기간'} rules={[{ type: 'array' as const, required: (projectStep === 'B' || projectStep === 'A'), message: '' }]}>
                    <RangePicker format="YYYY-MM-DD" onChange={onChangePeriod} style={{width:'250px'}} />                                   
                </Form.Item>  
                <Form.Item label={'교육 대상'}>
                    <LectureTarget form={form} />
                </Form.Item>  
                <Form.Item label={'강의요청서'}>
                    <NotionUrl form={form} url={projectInfo?.notion_url || ''} />   
                </Form.Item> 
                {(pType === 'confirm')?(
                <>
                    <Form.Item name="project_step" label={'상태'} rules={[{ required: true, message: '' }]}>
                        <Select placeholder="상태 선택" onSelect={((value)=>setProjectStep(value))}>
                            <Select.Option key={'A'} value={'A'}>A_확정</Select.Option>
                            {(id > 0) && (<Select.Option key={'B'} value={'B'}>B_일정논의</Select.Option>)}
                            {(id > 0) && (<Select.Option key={'D'} value={'D'}>D_드롭</Select.Option>)}
                        </Select>                                    
                    </Form.Item> 
                    <Form.Item name={'pm'} label={'PM'} rules={[{ required: true, message: '' }]}>
                        <Pm fieldname={'pm'} required={true} pm={projectInfo?.pm}/>   
                    </Form.Item>    
                </>                
                ):(pType === 'consult')?(
                    <>
                        <Form.Item name="project_step" label={'상태'} rules={[{ required: true, message: '' }]}>
                            <Select placeholder="상태 선택" onSelect={((value)=>setProjectStep(value))}>
                                {(id > 0) && (<Select.Option key={'A'} value={'A'}>A_확정</Select.Option>)}
                                <Select.Option key={'B'} value={'B'}>B_일정논의</Select.Option>
                                <Select.Option key={'C'} value={'C'}>C_진행협의</Select.Option>
                                {(id > 0) && (<Select.Option key={'D'} value={'D'}>D_드롭</Select.Option>)}
                            </Select>                                        
                        </Form.Item>   
                        <Form.Item name={'pm'} label={'PM'} rules={[{ required: true, message: '' }]}>
                            <Pm fieldname={'pm'} pm={projectInfo?.pm}/>   
                        </Form.Item>                                          
                    </>
                ):(
                    <>
                        <Form.Item name="project_step" label={'상태'} rules={[{ required: true, message: '' }]}>
                            <Select placeholder="상태 선택" onSelect={((value)=>setProjectStep(value))}>
                                <Select.Option key={'A'} value={'A'}>A_확정</Select.Option>
                                <Select.Option key={'B'} value={'B'}>B_일정논의</Select.Option>
                                <Select.Option key={'C'} value={'C'}>C_진행협의</Select.Option>
                                <Select.Option key={'D'} value={'D'}>D_드롭</Select.Option>
                            </Select>                                    
                        </Form.Item> 
                        <Form.Item name={'pm'} label={'PM'} rules={[{ required: true, message: '' }]}>
                            <Pm fieldname={'pm'} pm={projectInfo?.pm}/>   
                        </Form.Item>                           
                    </>
                    )}
                {(projectStep === 'D') && (
                <Form.Item  label={<label className='ant-form-item-required'>드롭사유</label>} rules={[{ required: false, message: '' }]}>
                    <Row>
                        <Col span={6}>
                            <Form.Item name="drop_reason" rules={[{ required: true, message: '' }]}>
                                <Select placeholder="드롭 사유를 선택" onSelect={((value)=>setDropReason(value))}>
                                    <Select.Option value={'단순문의'}>단순문의</Select.Option>
                                    <Select.Option value={'일정불가'}>일정불가</Select.Option>
                                    <Select.Option value={'비용문제'}>비용문제</Select.Option>
                                    <Select.Option value={'사업(주제)영역외'}>사업(주제)영역외</Select.Option>
                                    <Select.Option value={'타기관/자체진행'}>타기관/자체진행</Select.Option>
                                    <Select.Option value={'진행취소'}>진행취소</Select.Option>
                                    <Select.Option value={'일정촉박'}>일정촉박</Select.Option>
                                </Select>                                 
                            </Form.Item>                    
                        </Col>
                        <Col span={18}>
                        {(dropReason === '진행취소') && (
                            <Form.Item name="drop_reason_comment" rules={[{ required: true, message: '' }]}>
                                <Input style={{marginLeft:"5px"}} type="text" placeholder='진행취소 사유를 입력하세요'/>                                        
                            </Form.Item>                         
                        )}
                        </Col>
                    </Row>                    
                </Form.Item>                    
                )}
                <Row>
                    <Form.Item label={'매출'} style={{ width:'100%', display: 'inline-block'}}>
                        <label>{maxPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>
                        <label>&nbsp;({projectInfo?.calc_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || 0})</label>
                    </Form.Item>   
                </Row>                
                <Row>
                    <Form.Item name='lecturer_price' label={'강사 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('lecturer_price', value);onCalcMaxPirce();})}/>
                    </Form.Item>   
                    <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                            <label>{projectInfo?.calc_lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>
                    </Form.Item>                                
                </Row>
                <Row>
                    <Col span={12}> 
                        <Form.Item name='teaching_price' label={'교재비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('teaching_price', value);onCalcMaxPirce();})}/>                                             
                        </Form.Item>   
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_teaching_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                   
                        </Form.Item>                           
                    </Col>                                
                    <Col span={12}>
                        <Form.Item name='traffic_price' label={'교통비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('traffic_price', value);onCalcMaxPirce();})}/>                  
                        </Form.Item> 
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_traffic_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                      
                        </Form.Item>                          
                    </Col>                     
                </Row>  
                <Row>
                    <Col span={12}> 
                        <Form.Item name='plan_price' label={'설계비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('plan_price', value);onCalcMaxPirce();})}/>                   
                        </Form.Item>   
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_plan_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                  
                        </Form.Item>                          
                    </Col>            
                    <Col span={12}>
                        <Form.Item name='operating_price' label={'운영비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('operating_price', value);onCalcMaxPirce();})}/>                 
                        </Form.Item> 
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_operating_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                   
                        </Form.Item>                          
                    </Col> 
                </Row>   
                <Row>
                    <Col span={12}> 
                        <Form.Item name='diagnose_price' label={'HSG진단 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('diagnose_price', value);onCalcMaxPirce();})}/>                    
                        </Form.Item>  
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_diagnose_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                    
                        </Form.Item>                           
                    </Col>            
                    <Col span={12}>
                        <Form.Item name='consulting_price' label={'HSG컨설팅 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('consulting_price', value);onCalcMaxPirce();})}/>                  
                        </Form.Item> 
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_consulting_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                     
                        </Form.Item>                          
                    </Col> 
                </Row>                  
                <Row>
                    <Col span={12}>
                        <Form.Item name='studio_price' label={'스튜디오비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('studio_price', value);onCalcMaxPirce();})}/>                 
                        </Form.Item> 
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_studio_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                   
                        </Form.Item>                          
                    </Col>                     
                    <Col span={12}> 
                        <Form.Item name='etc_price' label={'기타비 매출'} style={{ width:'250px', display: 'inline-block'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('etc_price', value);onCalcMaxPirce();})}/>                     
                        </Form.Item> 
                        <Form.Item style={{ width:'calc(100% - 250px)', display: 'inline-block'}}>
                        <label>{projectInfo?.calc_etc_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>                      
                        </Form.Item>                            
                    </Col>            
                </Row>                                       
                <Form.Item name='comment' label={'메모'}>
                    <TextArea rows={3} placeholder='메모를 입력하세요'/>   
                </Form.Item>  
                <ButtonGroup className={'buttonsWrapper'}>
                    <div>
                        <Button onClick={onCancel} style={{marginRight: "5px"}}>취소</Button>
                    </div>                     
                    <div>
                        {(id>0 && !!projectInfo && projectInfo.bill_price === 0) && (
                            <Popconfirm
                                title="삭제 하시겠습니까?"
                                okText="삭제"
                                cancelText="취소"
                                onConfirm={() => { projectDelete({id:id})}}
                            >   
                                <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                            </Popconfirm>
                        )}
                        <Button htmlType="submit"  type="primary">{id===0?'등록':'수정'}</Button>
                    </div>                      
                </ButtonGroup>                
            </Form>            
        </ModalWrapper>  
        <CheckDuplicateModal
            title="프로젝트 상태 변경 확인"
            message={message}
            visible={isCheckConfirm}
            onOk={() => onCheckPopupClick(true)}
            onCancel={() => onCheckPopupClick(false)}
        />
        <CheckProjectStepModal
            title="프로젝트 기존 스케줄 삭제 확인"
            message={'기존 스케줄을 삭제 하시겠습니까?\n삭제를 원하시면 삭제를 눌러 주세요.'}
            visible={isCheckConfirm2}
            onOk={() => onCheckPopupClick2(true)}
            onCancel={() => onCheckPopupClick2(false)}
        />
    </>
    )    
};

export default React.memo(ProjectEditModal);    